import React from "react"
import { useNavigationContext } from "./navigation-context"
import { EXPANDED_WIDTH } from "./navigation-utils"
import { NavigationDockTrigger } from "./navigation-dock-trigger"
import { cn } from "../utils/cn"

export type { NavigationDockTriggerProps } from "./navigation-dock-trigger"

// =============================================================================
// Props
// =============================================================================

export type NavigationDockProps = {
  children?: React.ReactNode
  className?: string
}

// =============================================================================
// Styles
// =============================================================================

const DOCK_Z_INDEX = 10

const dockStyles = [
  "fixed bottom-0 inset-x-0 z-(--navigation-dock-z)",
  "p-4",
  "transition-[transform] duration-[250ms] ease-out",
  "bg-gradient-to-b from-white/0 to-white",
  // Gradient area is non-interactive; children restore pointer events individually
  "pointer-events-none"
]

// =============================================================================
// Root
// =============================================================================

/**
 * A floating bottom navigation bar for mobile viewports.
 *
 * Compose with `Navigation.Dock.Trigger`.
 * Must be rendered inside `Navigation` (with the `mobile` prop) so that `Dock.Trigger`
 * shares collapsed state with the sidebar.
 *
 * @example
 * ```tsx
 * <Navigation mobile currentPath={currentPath}>
 *   <Navigation.Sidebar>...</Navigation.Sidebar>
 *   <Navigation.Dock>
 *     <Navigation.Dock.Trigger />
 *   </Navigation.Dock>
 * </Navigation>
 * ```
 */
function NavigationDockRoot({ children, className }: NavigationDockProps) {
  const { collapsed, mobile, hidden, setCollapsed } = useNavigationContext()

  if (!mobile || hidden) return null

  return (
    <>
      {!collapsed && (
        <div
          className="fixed inset-y-0 right-0"
          style={{ left: EXPANDED_WIDTH }}
          onClick={() => setCollapsed(true)}
          aria-hidden
        />
      )}
      <div
        data-slot="navigation-dock"
        className={cn(dockStyles, className)}
        data-expanded={!collapsed ? "" : undefined}
        style={
          {
            "--navigation-dock-z": DOCK_Z_INDEX,
            transform: !collapsed
              ? `translateX(${EXPANDED_WIDTH})`
              : "translateX(0px)"
          } as React.CSSProperties
        }
      >
        {children}
      </div>
    </>
  )
}

NavigationDockRoot.displayName = "Navigation.Dock"

// =============================================================================
// Compound component
// =============================================================================

interface NavigationDockComponent {
  (props: NavigationDockProps): React.JSX.Element | null
  Trigger: typeof NavigationDockTrigger
}

export const NavigationDock: NavigationDockComponent = Object.assign(
  NavigationDockRoot,
  {
    Trigger: NavigationDockTrigger
  } as const
)
